import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { type ReactElement } from 'react';
import { pick as rPick, forEachObj as rForEachObj } from 'remeda';

import { SecondaryActionsIconButton } from '@/components/secondary-actions-icon-button.component';
import { SESSION_STORAGE } from '@/constants/storage';
import { useFormDraft } from '@/hooks/use-form-draft.hook';
import { CampaignRequestDistributionEnum, CampaignTypeEnum, CreativeTypeEnum } from '@/libs/openapi';
import {
  HelpCircleStrokeIcon,
  PROGRESS_INDICATOR_MIN_DELAY_DURATION,
  Text,
  TOAST_TIMEOUT,
  minDelay,
  tryCatch,
} from '@/libs/serenity-ui';
import { RouterMenuItemLink } from '@/libs/tanstack-router';
import { auditLogQueries } from '@/resources/audit-log/audit-log.api';
import { campaignRequestMutations, campaignRequestQueries } from '@/resources/campaign-request/campaign-request.api';
import { toApiCampaignRequestFieldAdapter } from '@/resources/campaign-request/campaign-request.field-adapter';
import { campaignRequestPolicies } from '@/resources/campaign-request/campaign-request.policies';
import { organizationTeamPolicies } from '@/resources/organization-team/organization-team.policies';
import { CampaignRequestForm, type CampaignFormValues } from '@/routes/_main/_form/campaigns/-components/campaign-request-form.component';
import { Resource } from '@/types/resource.type';
import { timezone } from '@/utils/timezone.util';

export const Route = createFileRoute('/_main/_form/campaigns/create/')({
  component: RouteComponent,
  staticData: {
    form: {
      heading: 'Add campaign',
      toolbarLayout: 'centered',
      goBackFallbackPath: '/campaigns',
      slots: {
        actionsComponent: ActionsComponent,
      },
    },
  },
  beforeLoad: async ({ context }) => {
    if (context.user && !context.can.campaignRequests.view()) {
      throw redirect({ to: '/' });
    }

    const can = {
      [Resource.campaignRequest as const]: rPick(campaignRequestPolicies.bindContext({ user: context.user.original, organization: context.organization?.original }), [ 'create', 'update' ]),
      [Resource.organizationTeam as const]: rPick(organizationTeamPolicies.bindContext({ user: context.user.original }), [ 'create' ]),
    };

    if (!can.campaignRequest.create()) {
      throw redirect({ to: '/campaigns' });
    }

    return { can };
  },
  head: () => ({
    meta: [
      {
        title: 'TrendMD | Create campaign',
      },
    ],
  }),
});

function ActionsComponent(): ReactElement {
  return (
    <SecondaryActionsIconButton>
      <RouterMenuItemLink to="/campaigns/create/guide">
        <HelpCircleStrokeIcon />
        <Text>Help</Text>
      </RouterMenuItemLink>
    </SecondaryActionsIconButton>
  );
}

function RouteComponent(): React.ReactElement {
  const navigate = Route.useNavigate();
  const routeContext = Route.useRouteContext();

  const createMutation = useMutation(campaignRequestMutations.create);
  const submitMutation = useMutation(campaignRequestMutations.submit);

  const form = useForm({
    defaultValues: {
      campaignName: '',
      customId: '',
      targetingType: undefined,
      npiFile: undefined,
      creativeFile: undefined,
      specialtyTargeting: '',
      geoTargeting: '',
      totalBudget: undefined,
      cost: undefined,
      campaignType: CampaignTypeEnum.CPC,
      clickGoals: undefined,
      timezone: timezone.get(),
      startDate: undefined,
      endDate: undefined,
      distribution: undefined,
      monthlyBudget: {},
      ownerUserId: undefined,
      teamId: undefined,
      ownerSearchInput: '',
      teamSearchInput: '',
    } as CampaignFormValues,
    onSubmit: async ({ value }) => {
      const body = toApiCampaignRequestFieldAdapter.transform({
        campaignName: value.campaignName,
        startDate: value.startDate!,
        endDate: value.endDate!,
        timezone: value.timezone,
        totalBudget: value.totalBudget ?? 0,
        cost: value.cost ?? 0,
        campaignType: value.campaignType,
        customId: value.customId || undefined,
        targetingType: value.targetingType,
        npiFileId: value.npiFile?.id,
        creativeFileId: value.creativeFile?.id,
        specialtyTargeting: value.specialtyTargeting || undefined,
        geoTargeting: value.geoTargeting || undefined,
        creativeType: CreativeTypeEnum.LINK,
        distribution: value.distribution,
        monthlyBudget: value.distribution === CampaignRequestDistributionEnum.MONTHLY ? value.monthlyBudget : undefined,
        ownerUserId: value.ownerUserId,
        teamId: value.teamId,
      });

      const createResponse = await tryCatch(minDelay(createMutation.mutateAsync(body), PROGRESS_INDICATOR_MIN_DELAY_DURATION));

      if (createResponse.error) {
        routeContext.toastQueue.add({ message: createResponse.error.message, variant: 'error', actionProps: { children: 'Retry', onPress: () => form.handleSubmit() }}, { timeout: TOAST_TIMEOUT });
        return;
      }

      const submitResponse = await tryCatch(submitMutation.mutateAsync(createResponse.data.id));

      clearDraft();
      routeContext.queryClient.invalidateQueries(campaignRequestQueries.all);
      routeContext.queryClient.invalidateQueries(auditLogQueries.all);

      if (submitResponse.error) {
        routeContext.toastQueue.add({ message: 'Submission failed, campaign saved as draft.', variant: 'warning' }, { timeout: TOAST_TIMEOUT });
        navigate({ to: '/campaigns/draft/$campaignId', params: { campaignId: createResponse.data.id }});
      } else {
        routeContext.toastQueue.add({ message: 'Campaign was submitted for review.', variant: 'success' }, { timeout: TOAST_TIMEOUT });
        navigate({ to: '/campaigns/$campaignId', params: { campaignId: createResponse.data.id }});
      }
    },
  });

  const { hasDraft, restoreDraft, clearDraft, cancelDraft } = useFormDraft<CampaignFormValues>({
    storageKey: SESSION_STORAGE.campaignRequestCreateDraft.key,
    subscribe: (cb) => form.store.subscribe(({ currentVal }) => !currentVal.isDefaultValue && cb(currentVal.values)),
    restoreValues: rForEachObj((value, key) => form.setFieldValue(key, value)),
  });

  const handleSaveAsDraft = async (): Promise<void> => {
    const errors = await form.validateField('campaignName', 'blur');
    if (errors.length > 0) {
      return;
    }

    const body = toApiCampaignRequestFieldAdapter.transform({
      campaignName: form.state.values.campaignName,
      startDate: form.state.values.startDate ?? undefined,
      endDate: form.state.values.endDate ?? undefined,
      timezone: (form.state.values.startDate || form.state.values.endDate) ? form.state.values.timezone : undefined,
      totalBudget: form.state.values.totalBudget ?? undefined,
      cost: form.state.values.cost ?? undefined,
      campaignType: form.state.values.campaignType,
      customId: form.state.values.customId || undefined,
      targetingType: form.state.values.targetingType,
      npiFileId: form.state.values.npiFile?.id,
      creativeFileId: form.state.values.creativeFile?.id,
      specialtyTargeting: form.state.values.specialtyTargeting || undefined,
      geoTargeting: form.state.values.geoTargeting || undefined,
      creativeType: form.state.values.creativeFile ? CreativeTypeEnum.LINK : undefined,
      distribution: form.state.values.distribution,
      monthlyBudget: form.state.values.distribution === CampaignRequestDistributionEnum.MONTHLY ? form.state.values.monthlyBudget : undefined,
      ownerUserId: form.state.values.ownerUserId,
      teamId: form.state.values.teamId,
    });

    const response = await tryCatch(minDelay(createMutation.mutateAsync(body), PROGRESS_INDICATOR_MIN_DELAY_DURATION));

    if (response.error) {
      routeContext.toastQueue.add({ message: response.error.message, variant: 'error' }, { timeout: TOAST_TIMEOUT });
      return;
    }

    clearDraft();
    routeContext.queryClient.invalidateQueries(campaignRequestQueries.all);
    routeContext.queryClient.invalidateQueries(auditLogQueries.all);
    routeContext.toastQueue.add({ variant: 'success', message: 'Saved as a draft. You can update and submit it for review later.' }, { timeout: TOAST_TIMEOUT });
    navigate({ to: '/campaigns/draft/$campaignId', params: { campaignId: response.data.id }});
  };

  return (
    <CampaignRequestForm
      canUpdate
      cancelDraft={cancelDraft}
      form={form}
      hasDraft={hasDraft}
      isSavePending={createMutation.isPending}
      restoreDraft={restoreDraft}
      routeContext={routeContext}
      onSaveDraft={handleSaveAsDraft}
    />
  );
}
